/*
 * Pickup availability — sucursal seleccionada
 *
 * El bloque nativo de Shopify siempre muestra `pick_up_availabilities.first`
 * (el orden lo decide Shopify, no el usuario), así que la vista previa podía
 * decir "Recogida disponible en SALITRE" mientras el header decía PEREIRA
 * CERRITOS. Este script reescribe ese bloque sobre la sucursal elegida:
 *
 *   1. Vista previa: nombra la sucursal seleccionada en vez de la primera.
 *   2. Drawer: ordena seleccionada → misma ciudad → resto.
 *   3. Drawer: agrega las unidades disponibles por sucursal.
 *
 * El HTML del bloque lo inyecta pickup-availability.js tras un fetch de la
 * sección, por eso observamos el DOM en vez de correr una sola vez.
 */
(function () {
  'use strict';

  if (window.__pickupSucursalReady) return;
  window.__pickupSucursalReady = true;

  var STORAGE_KEY = 'sucursal_store';
  var ITEM_UNAVAILABLE_TEXT = 'No disponible en esta sucursal';
  var PREVIEW_UNAVAILABLE_TEXT = 'No disponible en ';
  var API = 'https://sioconn.dislicores.com/poligonos';

  // Las locations se piden por ciudad, así que para saber la ciudad de CADA
  // sucursal hay que recorrer todas las ciudades. Es caro: lo hacemos una
  // sola vez por carga y lo memorizamos en esta promesa.
  var locationsPromise = null;
  // Inventario cacheado por variante: el drawer se reabre y las variantes se
  // cambian sin recargar la página.
  var inventoryCache = {};

  function escapeHtml(value) {
    return String(value || '').replace(/[&<>"']/g, function (ch) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch];
    });
  }

  function getState() {
    try {
      var raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null;
    }
  }

  function normalizeId(value) {
    if (!value) return '';
    return String(value).split('/').pop();
  }

  function normalizar(value) {
    return String(value || '')
      .trim()
      .toLowerCase()
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '');
  }

  // Mismo criterio que sucursal.js: "Bogotá, D.C." y "Bogotá" son una ciudad.
  function ciudadCoincide(a, b) {
    var x = normalizar(a);
    var y = normalizar(b);
    if (!x || !y) return false;
    return x === y || x.indexOf(y) === 0 || y.indexOf(x) === 0;
  }

  // ── CATÁLOGO DE SUCURSALES ────────────────────────────────────
  // Devuelve { nombreNormalizado: { id, nombre, ciudad } } para poder cruzar
  // los nombres que imprime Liquid con la ciudad y el id de cada location.
  function loadLocations() {
    if (locationsPromise) return locationsPromise;

    locationsPromise = fetch(API + '/ciudades', { headers: { Accept: 'application/json' } })
      .then(function (r) {
        if (!r.ok) throw new Error('ciudades');
        return r.json();
      })
      .then(function (ciudades) {
        if (!Array.isArray(ciudades)) return [];

        return Promise.all(
          ciudades.map(function (ciudad) {
            return fetch(API + '/locations?ciudad=' + encodeURIComponent(ciudad), {
              headers: { Accept: 'application/json' }
            })
              .then(function (r) {
                return r.ok ? r.json() : [];
              })
              .catch(function () {
                return [];
              });
          })
        );
      })
      .then(function (grupos) {
        var index = {};

        (grupos || []).forEach(function (lista) {
          (lista || []).forEach(function (loc) {
            var key = normalizar(loc.nombre);
            if (key && !index[key]) index[key] = loc;
          });
        });

        return index;
      })
      .catch(function () {
        // Sin catálogo seguimos ordenando por nombre de la sucursal elegida;
        // solo perdemos el agrupado por ciudad.
        return {};
      });

    return locationsPromise;
  }

  // Solo un numero finito real cuenta como dato. undefined, null, '' o NaN
  // significan "el endpoint no lo dijo", nunca "hay cero".
  function toQty(value) {
    if (value === null || value === undefined || value === '') return null;
    var n = Number(value);
    return isFinite(n) ? n : null;
  }

  // ── INVENTARIO ────────────────────────────────────────────────
  function fetchInventory(locationId, productId, variantId) {
    var cacheKey = normalizeId(locationId) + ':' + normalizeId(variantId || productId);

    if (Object.prototype.hasOwnProperty.call(inventoryCache, cacheKey)) {
      return Promise.resolve(inventoryCache[cacheKey]);
    }

    return fetch(
      API +
        '/inventory?locationId=' +
        encodeURIComponent(locationId) +
        '&productIds=' +
        encodeURIComponent(normalizeId(productId)),
      { headers: { Accept: 'application/json' } }
    )
      .then(function (r) {
        if (!r.ok) throw new Error('inventory');
        return r.json();
      })
      .then(function (products) {
        var product = Array.isArray(products) ? products[0] : null;
        var qty = null;

        if (product) {
          var variants = Array.isArray(product.variants) ? product.variants : [];
          var wanted = normalizeId(variantId);

          var match = wanted
            ? variants.filter(function (v) {
                return normalizeId(v.variantId) === wanted;
              })[0]
            : null;

          if (match) {
            qty = toQty(match.available);
          } else if (wanted && variants.length) {
            // Pedimos una variante concreta y el endpoint devolvio otras: el
            // cruce fallo. Decir "sin stock" aqui seria inventar un cero.
            qty = null;
          } else {
            // Variante unica, o no sabemos cual pedir: el total es la mejor
            // respuesta disponible.
            qty = toQty(product.totalAvailable);
          }
        }

        inventoryCache[cacheKey] = qty;
        return qty;
      })
      .catch(function () {
        // null = "no sabemos", distinto de 0 = "sin stock". No pintamos nada.
        return null;
      });
  }

  // ── CONTEXTO DEL PRODUCTO ─────────────────────────────────────
  function getProductContext() {
    var host = document.querySelector('pickup-availability[data-variant-id]');
    var variantId = host ? host.getAttribute('data-variant-id') : '';
    var productId = '';

    // product-info es el contenedor de la PDP; un [data-product-id] suelto
    // podría ser una tarjeta de recomendados.
    var meta = document.querySelector('product-info[data-product-id]');
    if (meta) productId = meta.getAttribute('data-product-id') || '';

    if (!productId) {
      // Fallback: el JSON de producto que Shopify deja en la PDP.
      var node = document.querySelector('script[type="application/json"][data-product-json], #ProductJson-product');
      if (node) {
        try {
          var data = JSON.parse(node.textContent);
          if (data && data.id) productId = String(data.id);
        } catch (e) {}
      }
    }

    return { productId: normalizeId(productId), variantId: normalizeId(variantId) };
  }

  // ── VISTA PREVIA ──────────────────────────────────────────────
  // Solo renombramos si la sucursal elegida está de verdad en la lista de
  // recogida. Si no aparece, Shopify no ofrece recogida ahí y afirmarlo sería
  // mentir: en ese caso se deja el texto original (la sucursal más cercana).
  function selectedOffersPickup(state) {
    var drawer = document.querySelector('pickup-availability-drawer');
    if (!drawer) return false;

    var items = drawer.querySelectorAll('.pickup-availability-list__item');
    var target = normalizar(state.nombre);

    for (var i = 0; i < items.length; i++) {
      if (normalizar(getItemName(items[i])) === target) return true;
    }

    return false;
  }

  function updatePreview(state) {
    var preview = document.querySelector('pickup-availability pickup-availability-preview');
    if (!preview || !state || !state.nombre) return;

    var target = preview.querySelector('.pickup-availability-info__location');
    if (!target || target.dataset.sucursalApplied === state.nombre) return;
    if (!selectedOffersPickup(state)) return;

    // Solo se sustituye el nombre de la tienda; el resto de la frase
    // ("Recogida disponible en …") viene de pick_up_available_at_html, que
    // envuelve el nombre en <span class="color-foreground">.
    var html = target.innerHTML;
    var replaced = html.replace(/(<span[^>]*>)([\s\S]*?)(<\/span>)/i, '$1' + escapeHtml(state.nombre) + '$3');

    if (replaced === html) {
      // Sin el <span> esperado reconstruimos la frase completa.
      replaced = 'Recogida disponible en <span class="color-foreground">' + escapeHtml(state.nombre) + '</span>';
    }

    target.innerHTML = replaced;
    target.dataset.sucursalApplied = state.nombre;
  }

  // El inventario real llega despues del render, asi que la vista previa se
  // corrige aparte: sin unidades en la sucursal elegida no hay recogida ahi.
  function updatePreviewStock(state, qty) {
    if (qty === null || qty === undefined || !state || !state.nombre) return;

    var preview = document.querySelector('pickup-availability pickup-availability-preview');
    if (!preview) return;

    var target = preview.querySelector('.pickup-availability-info__location');
    if (!target) return;

    var time = preview.querySelector('.pickup-availability-info__pickup-time');

    if (qty > 0) {
      if (target.dataset.sucursalEmpty !== 'true') return;

      if (target.dataset.sucursalStocked) target.innerHTML = target.dataset.sucursalStocked;
      delete target.dataset.sucursalEmpty;
      if (time) time.hidden = false;
      return;
    }

    if (target.dataset.sucursalEmpty === 'true') return;

    target.dataset.sucursalStocked = target.innerHTML;
    target.innerHTML =
      PREVIEW_UNAVAILABLE_TEXT + '<span class="color-foreground">' + escapeHtml(state.nombre) + '</span>';
    target.dataset.sucursalEmpty = 'true';
    if (time) time.hidden = true;
  }

  // ── DRAWER ────────────────────────────────────────────────────
  function getItemName(item) {
    var title = item.querySelector('h3');
    return title ? title.textContent.trim() : '';
  }

  function sortDrawer(list, state, locIndex) {
    var items = Array.prototype.slice.call(list.querySelectorAll('.pickup-availability-list__item'));

    // Con una sola sucursal no hay nada que ordenar, pero igual devolvemos la
    // forma { item } que espera decorateDrawer para pintarle el inventario.
    if (items.length < 2) {
      return items.map(function (item, i) {
        return { item: item, rank: 0, ciudad: '', index: i };
      });
    }

    var selectedName = normalizar(state && state.nombre);
    var selectedCity = (state && state.ciudad) || '';

    // rank: 0 = sucursal elegida, 1 = su misma ciudad, 2 = el resto.
    var ranked = items.map(function (item, i) {
      var nombre = getItemName(item);
      var key = normalizar(nombre);
      var loc = locIndex[key];
      var ciudad = loc ? loc.ciudad : '';

      var rank = 2;
      if (selectedName && key === selectedName) {
        rank = 0;
      } else if (selectedCity && ciudad && ciudadCoincide(ciudad, selectedCity)) {
        rank = 1;
      }

      return { item: item, rank: rank, ciudad: ciudad, index: i };
    });

    ranked.sort(function (a, b) {
      if (a.rank !== b.rank) return a.rank - b.rank;
      return a.index - b.index; // empate: conservamos el orden de Shopify.
    });

    var frag = document.createDocumentFragment();
    ranked.forEach(function (entry) {
      if (entry.rank === 0) entry.item.classList.add('pickup-availability-list__item--selected');
      frag.appendChild(entry.item);
    });

    list.appendChild(frag);
    return ranked;
  }

  function renderStock(item, qty) {
    if (qty === null || qty === undefined) return;

    var node = item.querySelector('.pickup-availability-list__stock');

    if (!node) {
      node = document.createElement('p');
      node.className = 'pickup-availability-list__stock text-sm';

      var info = item.querySelector('.pickup-availability-list__item-info');
      if (info && info.parentNode) {
        info.parentNode.insertBefore(node, info.nextSibling);
      } else {
        item.appendChild(node);
      }
    }

    if (qty > 0) {
      node.textContent = qty === 1 ? '1 unidad disponible' : qty + ' unidades disponibles';
      node.classList.remove('pickup-availability-list__stock--empty');
    } else {
      node.textContent = 'Sin unidades disponibles';
      node.classList.add('pickup-availability-list__stock--empty');
    }

    renderItemStatus(item, qty);
  }

  // Liquid pinta "Recogida disponible, ..." segun el inventario que conoce
  // Shopify; el poligono manda. Con cero unidades la linea de estado pasa a
  // decir que no hay recogida en esa sucursal.
  function renderItemStatus(item, qty) {
    // Sin dato del endpoint respetamos lo que Liquid ya pinto.
    if (qty === null || qty === undefined) return;

    var info = item.querySelector('.pickup-availability-list__item-info');
    if (!info) return;

    if (qty > 0) {
      if (info.dataset.sucursalEmpty !== 'true') return;

      if (info.dataset.sucursalOriginal) info.innerHTML = info.dataset.sucursalOriginal;
      delete info.dataset.sucursalEmpty;
      info.classList.remove('pickup-availability-list__item-info--unavailable');
      return;
    }

    if (info.dataset.sucursalEmpty === 'true') return;

    if (!info.dataset.sucursalOriginal) info.dataset.sucursalOriginal = info.innerHTML;
    info.textContent = ITEM_UNAVAILABLE_TEXT;
    info.dataset.sucursalEmpty = 'true';
    info.classList.add('pickup-availability-list__item-info--unavailable');
  }

  function decorateDrawer(drawer, state, locIndex) {
    var list = drawer.querySelector('[data-store-availability-drawer-content]');
    if (!list) return;

    var ranked = sortDrawer(list, state, locIndex);
    var ctx = getProductContext();
    if (!ctx.productId) return;

    ranked.forEach(function (entry) {
      var loc = locIndex[normalizar(getItemName(entry.item))];
      if (!loc || !loc.id) return;

      fetchInventory(loc.id, ctx.productId, ctx.variantId).then(function (qty) {
        if (entry.item.isConnected) renderStock(entry.item, qty);

        // La vista previa habla de la sucursal elegida: si es esta y quedo en
        // cero, deja de prometer recogida.
        if (entry.rank === 0) updatePreviewStock(state, qty);
      });
    });
  }

  // ── APLICACIÓN + OBSERVER ─────────────────────────────────────
  function apply() {
    var state = getState();
    if (!state || !state.nombre) return;

    updatePreview(state);

    var drawer = document.querySelector('pickup-availability-drawer');
    if (!drawer || drawer.dataset.sucursalDecorated === 'true') return;

    drawer.dataset.sucursalDecorated = 'true';

    loadLocations().then(function (locIndex) {
      decorateDrawer(drawer, state, locIndex);
    });
  }

  function scheduleApply() {
    clearTimeout(scheduleApply.timer);
    scheduleApply.timer = setTimeout(apply, 60);
  }

  // pickup-availability.js reemplaza el preview y reinyecta el drawer en
  // <body> cada vez que cambia la variante, así que escuchamos el árbol.
  var observer = new MutationObserver(function (mutations) {
    for (var i = 0; i < mutations.length; i++) {
      var added = mutations[i].addedNodes;

      for (var j = 0; j < added.length; j++) {
        var node = added[j];
        if (node.nodeType !== 1) continue;

        var tag = node.tagName.toLowerCase();
        if (
          tag === 'pickup-availability-drawer' ||
          tag === 'pickup-availability-preview' ||
          node.querySelector('pickup-availability-preview, pickup-availability-drawer')
        ) {
          scheduleApply();
          return;
        }
      }
    }
  });

  function start() {
    observer.observe(document.body, { childList: true, subtree: true });
    scheduleApply();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }

  document.addEventListener('sucursal:changed', function () {
    inventoryCache = {};
    var drawer = document.querySelector('pickup-availability-drawer');
    if (drawer) delete drawer.dataset.sucursalDecorated;
    scheduleApply();
  });
})();
